import { useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';
import {
  ClinicInformationListResponse,
  UpdateEmailRequest,
  ChangePasswordRequest,
  ApiResponse,
} from '@/pages/main/clinic-information/types';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import useAppToast from '@hooks/useAppToast';

// ==================== API Service Functions ====================

/**
 * Get paginated list of clinic informations (by applicable period)
 */
const getList = async (
  siteType: string,
  clinicNumber: string,
  params: { per_page?: number; page?: number }
): Promise<ClinicInformationListResponse> => {
  const response = await apiClient.get<ClinicInformationListResponse>(
    clinicApiPath(siteType, clinicNumber, '/clinic-informations'),
    { params }
  );
  return response.data;
};

/**
 * Update email address for clinic information
 */
const updateEmail = async (
  siteType: string,
  clinicNumber: string,
  id: number,
  data: UpdateEmailRequest
): Promise<ApiResponse> => {
  const response = await apiClient.put<ApiResponse>(
    clinicApiPath(siteType, clinicNumber, `/clinic-informations/${id}/email`),
    data
  );
  return response.data;
};

/**
 * Change password for clinic user
 */
const changePassword = async (
  siteType: string,
  clinicNumber: string,
  data: ChangePasswordRequest
): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>(
    clinicApiPath(siteType, clinicNumber, '/clinic-informations/change-password'),
    data
  );
  return response.data;
};

// ==================== Hooks ====================

/**
 * Hook to get paginated list of clinic informations (one record per page by 適用期間).
 * Returns list data and loading state; fetch error is shown via toast inside the hook.
 */
export const useMainClinicInformationList = (page: number, options: { enabled?: boolean } = {}) => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, refetch, error, isFetching } = useQuery<
    ClinicInformationListResponse,
    Error
  >({
    queryKey: ['clinic-informations', 'list', tenant, clinicNumber, page],
    queryFn: () => getList(tenant, clinicNumber, { per_page: 1, page }),
    // `enabled: false` lets a caller that does not need the record skip the request (and its toast).
    enabled: (options.enabled ?? true) && !!tenant && !!clinicNumber && page >= 1,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error, isFetching };
};

/** Payload for update email mutation */
export type UpdateEmailPayload = { id: number; data: UpdateEmailRequest };

/**
 * Hook to update email address. Returns mutateAsync and loading state.
 * Success/error notifications and cache invalidation are handled inside useMutation.
 */
export const useMainClinicInformationUpdateEmail = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: updateEmailMutation, isPending: isLoading } = useMutation<
    ApiResponse,
    Error,
    UpdateEmailPayload
  >({
    mutationFn: ({ id, data }: UpdateEmailPayload) => updateEmail(tenant, clinicNumber, id, data),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['clinic-informations'] });
        toast.success(response.message ?? 'メールアドレスを更新しました。');
      } else {
        toast.error(response.message ?? '更新に失敗しました。');
      }
    },
    onError: error => {
      const msg = getApiErrorMessage(error, '更新に失敗しました。');
      toast.error(msg);
    },
  });

  return { updateEmailMutation, isLoading };
};

/**
 * Hook to change password. Returns mutateAsync and loading state.
 * Success/error notifications are handled inside useMutation.
 */
export const useMainClinicInformationChangePassword = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: changePasswordMutation, isPending: isLoading } = useMutation<
    ApiResponse,
    Error,
    ChangePasswordRequest
  >({
    mutationFn: (data: ChangePasswordRequest) => changePassword(tenant, clinicNumber, data),
    onSuccess: response => {
      if (response.success) {
        toast.success(response.message ?? 'パスワードを更新しました。');
      } else {
        toast.error(response.message ?? '更新に失敗しました。');
      }
    },
    onError: error => {
      const msg = getApiErrorMessage(error, '更新に失敗しました。');
      toast.error(msg);
    },
  });

  return { changePasswordMutation, isLoading };
};
